// Sticky paper-color picker. Lives next to TextFormatToolbar's text-color
// button but writes to `shape.color` (the canonical StickyColor key) rather
// than the textStyle map. Also stashes the user's pick in stickyPrefs so
// auto-create's color rotation resumes from the user's choice rather than
// the cycle's prior position.
//
// Open/close is *controlled* by the parent toolbar — the toolbar keeps a
// single `openPopover` slot so only one picker (text-color or sticky-color)
// can be open at a time. The component reports outside-click and Esc back
// via `onClose`.

import { useEffect, useId, useRef } from 'react';
import type * as Y from 'yjs';
import { STICKY_COLORS, type Sticky, type StickyColor } from '@frame/shared';
import { updateShape } from './binding/shapesMap';
import { writeStickyColor } from './stickyPrefs';

// Tailwind's bare `grid-cols-4` compiles to `repeat(4, minmax(0, 1fr))`,
// which collapses to 0px-wide columns inside an auto-width absolute
// container (the popover). Pinning each column to the swatch's 1.5rem
// keeps the grid honest regardless of how the container sizes itself.
// Do not "simplify" this to `grid-cols-4` — the picker visibly collapses
// to a stack of overlapping swatches. See commit 744f094 for the original
// regression; the StickyColorButtonGallery in StyleGuidePage is the
// visual regression surface (jsdom doesn't do layout, so this can only
// be caught by eye).
const STICKY_PICKER_GRID_CLASS = 'grid grid-cols-[repeat(4,1.5rem)] gap-1.5';

export interface StickyColorButtonProps {
  // The component takes the full Sticky shape (not just an id) so the type
  // system enforces the precondition that this picker only edits stickies.
  shape: Sticky;
  palette: Record<StickyColor, string>;
  doc: Y.Doc;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  // When true the parent toolbar is sitting BELOW its shape (because flipping
  // above would clip the viewport top). In that case the popover should open
  // upward so it doesn't clip the bottom of the viewport.
  flipUp?: boolean;
}

export function StickyColorButton({
  shape,
  palette,
  doc,
  open,
  onToggle,
  onClose,
  flipUp = false,
}: StickyColorButtonProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();
  const firstSwatchRef = useRef<HTMLButtonElement>(null);
  const current = shape.color;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const el = wrapRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // Focus the first swatch on open so keyboard users land on something they
  // can act on; deferred to the next frame so the popover is in the DOM.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => firstSwatchRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const pick = (color: StickyColor) => {
    updateShape(doc, shape.id, { color });
    writeStickyColor(color);
    onClose();
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        title="Sticky color"
        aria-label="Sticky color"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onClick={onToggle}
        onMouseDown={(e) => e.preventDefault()}
        className="
          h-7 w-7 inline-flex items-center justify-center rounded-2
          hover:bg-bg-hover transition-colors duration-micro ease-out
          focus-visible:outline-none focus-visible:shadow-focus
        "
      >
        <span
          aria-hidden
          className="w-4 h-4 rounded-sm border border-border-default"
          style={{ backgroundColor: palette[current] }}
        />
      </button>
      {open && (
        <div
          id={popoverId}
          role="dialog"
          aria-label="Sticky color"
          className={[
            'absolute left-1/2 -translate-x-1/2 w-fit p-2',
            flipUp ? 'bottom-full mb-1' : 'top-full mt-1',
            'bg-bg-panel border border-border-default rounded-3 shadow-md',
          ].join(' ')}
        >
          <div className={STICKY_PICKER_GRID_CLASS}>
            {STICKY_COLORS.map((name, i) => {
              const active = name === current;
              return (
                <button
                  key={name}
                  ref={i === 0 ? firstSwatchRef : undefined}
                  type="button"
                  aria-label={name}
                  aria-pressed={active}
                  title={name}
                  onClick={() => pick(name)}
                  onMouseDown={(e) => e.preventDefault()}
                  className={[
                    'w-6 h-6 rounded-sm border-2',
                    'transition-transform duration-micro hover:scale-110',
                    'focus-visible:outline-none focus-visible:shadow-focus',
                  ].join(' ')}
                  style={{
                    backgroundColor: palette[name],
                    borderColor: active ? 'var(--accent)' : 'transparent',
                  }}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
